// The screens a logged-out user moves between. Never recorded as, nor honoured
// as, a return target: coming back to one after login would loop.
export const AUTH_PATHS = ['/sign-in', '/forgot-password', '/reset-password', '/change-password'];

type PageLocation = { pathname: string; search: string; hash: string };

/** `/sign-in`, carrying the current page as `returnUrl` so login can come back to it. */
export const getSignInUrl = ({ pathname, search, hash }: PageLocation): string => {
  // Already on an auth screen: keep the query, which carries any returnUrl. A
  // logged-out load fires two redirects (AuthenticateRoute's <Navigate>, then
  // AuthContext's window.location), and by the second the URL is already
  // /sign-in?returnUrl=...; a bare /sign-in here threw the link away.
  if (AUTH_PATHS.includes(pathname)) return `/sign-in${search}`;
  if (pathname === '/') return '/sign-in';
  return `/sign-in?returnUrl=${encodeURIComponent(pathname + search + hash)}`;
};

/**
 * Where to go after login. `returnUrl` comes from the query string, so anyone can
 * craft it: only a path on this origin is honoured, or a shared link becomes an
 * open redirect (`//evil.com`, `/\evil.com` and `https://evil.com` all leave the site).
 */
export const getReturnUrl = (search: string, origin: string): string => {
  const raw = new URLSearchParams(search).get('returnUrl');
  if (!raw) return '/';

  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return '/';
  }

  if (url.origin !== origin || AUTH_PATHS.includes(url.pathname)) return '/';
  return url.pathname + url.search + url.hash;
};

// Pages that ask for the org themselves, so login skips its own org step on the
// way back to them. The MCP consent page scopes the token to the org picked there.
const PICKS_OWN_ORG_PATHS = ['/mcp/authorize'];

/** Whether login should skip the org step, given the `getReturnUrl` result. */
export const returnPicksOwnOrg = (returnUrl: string): boolean =>
  PICKS_OWN_ORG_PATHS.includes(returnUrl.split(/[?#]/)[0]);
